import { ProductError, ProductErrorType } from '../../models';

/**
 * As any video interactive might contain a game to avoid circular dependencies
 *  Critical interface (VideoInteractiveData, VideoInteraction and VideoGameItem) are moved in `data.ts`
 */

export class VideoChapter {
  from: string; // time code with the format "{min}:{sec}.[ms}", example: "0:01:15.000"
  to: string; // time code with the format "{min}:{sec}.[ms}", example: "0:01:15.000"
  name?: string;
  color: string; // code hexa of the background-color

  constructor(rawData?: VideoChapter) {
    this.from = rawData?.from;
    this.to = rawData?.to;
    this.name = rawData?.name;
    this.color = rawData?.color;
  }

  static validate(
    videoChapter: VideoChapter,
    parentPath: string,
    payload: any,
  ): { payload: any; type: ProductErrorType; property: string }[] {
    return ProductError.validatePrimitiveTypes(videoChapter, ['from', 'to', 'color'], null, parentPath).map(error => ({
      ...error,
      payload,
    }));
  }
}

export enum VideoInteractionType {
  QUIZ = 'quiz',
  LIST = 'list',
  GAME = 'game',
  DOWNLOAD = 'download',
  JUMP = 'jump',
}

export class VideoQuestion {
  sentence: string;
  type: VideoQuestionType;
  countdown?: string;
  weight?: number;
  correct?: VideoCorrection;
  wrong?: VideoCorrection;
  possibilities: VideoQuestionPossibility[];

  constructor(rawData?: VideoQuestion) {
    this.sentence = rawData?.sentence;
    this.type = rawData?.type;
    this.countdown = rawData?.countdown;
    this.weight = rawData?.weight ?? 1;
    this.correct = rawData?.correct && new VideoCorrection(rawData.correct);
    this.wrong = rawData?.wrong && new VideoCorrection(rawData.wrong);
    this.possibilities = (rawData?.possibilities || []).map(p => new VideoQuestionPossibility(p));
  }

  static validate(videoQuestion: VideoQuestion, parentPath: string, payload: any): ProductError[] {
    const required = ['sentence', 'type'];
    const errors: ProductError[] = ProductError.validatePrimitiveTypes(videoQuestion, required, null, parentPath) //
      .map(error => ({ ...error, payload }));
    if (videoQuestion.possibilities?.length > 0) {
      videoQuestion.possibilities
        .map((p, i) =>
          VideoQuestionPossibility.validate(p, `${parentPath}possibilities[${i}].`, {
            ...payload,
            possibilityIndex: i,
          }),
        )
        .reduce((flatArr, arr) => flatArr.concat(arr), [])
        .forEach(error => errors.push(error));
    } else {
      errors.push({ type: ProductErrorType.MISSING, property: `${parentPath}possibilities`, payload });
    }
    return errors;
  }
}

export enum VideoQuestionType {
  SINGLE = 'single',
  MULTIPLE = 'multiple',
}

export class VideoCorrection {
  type: VideoCorrectionType;
  href: string;

  constructor(rawData?: VideoCorrection) {
    this.type = rawData?.type;
    this.href = rawData?.href;
  }
}

export enum VideoCorrectionType {
  PODCAST = 'podcast',
  VIDEO = 'video',
}

export class VideoQuestionPossibility {
  text: string;
  correct: boolean;

  constructor(rawData?: VideoListItem) {
    this.text = rawData?.text || '';
    this.correct = rawData?.correct || false;
  }

  static validate(possibility: VideoQuestionPossibility, parentPath: string, payload: any): ProductError[] {
    return ProductError.validatePrimitiveTypes(possibility, ['text', 'correct'], null, parentPath) //
      .map(error => ({ ...error, payload }));
  }
}

export class VideoListItem {
  text: string;
  correct: boolean;

  constructor(rawData?: VideoListItem) {
    this.text = rawData?.text || '';
    this.correct = rawData?.correct || false;
  }

  static validate(listItem: VideoListItem, parentPath: string, payload: any): ProductError[] {
    if (!listItem) {
      return [{ type: ProductErrorType.MISSING, property: 'groups' }];
    }
    return ProductError.validatePrimitiveTypes(listItem, ['text', 'correct'], null, parentPath) //
      .map(error => ({ ...error, payload }));
  }
}

export class VideoDownloadOption {
  src: string;
  title?: string;
  label?: string;

  constructor(rawData?: VideoDownloadOption) {
    this.src = rawData?.src;
    this.title = rawData?.title;
    this.label = rawData?.label;
  }

  static validate(downloadOptions: VideoDownloadOption, parentPath: string, payload: any): ProductError[] {
    return ProductError.validatePrimitiveTypes(downloadOptions, ['src'], null, parentPath) //
      .map(error => ({ ...error, payload }));
  }
}

export class VideoJumpOption {
  label: string;
  to: string; // time code with the format "{min}:{sec}.{ms}", example: "0:01:15.000"

  constructor(rawData?: VideoJumpOption) {
    this.label = rawData?.label;
    this.to = rawData?.to;
  }

  static validate(jumpOptions: VideoJumpOption, parentPath: string, payload: any): ProductError[] {
    return ProductError.validatePrimitiveTypes(jumpOptions, ['label', 'to'], null, parentPath) //
      .map(error => ({ ...error, payload }));
  }
}
